'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdSearch,
    MdClose,
    MdImage,
    MdRefresh,
    MdFilterList,
    MdVisibility,
    MdLink,
    MdOpenInNew,
    MdPerson,
} from 'react-icons/md'
import {
    getDicomStudies,
    getStudyViewerUrl,
    DicomStudy,
    DICOM_MODALITY_LABELS,
    DICOM_MODALITY_SHORT_LABELS,
    DicomModality,
} from '@/lib/api/health-integrations/pacs-api'

export default function DicomStudiesPage() {
    const { user } = useAuth()
    const navigate = useNavigate()

    const [studies, setStudies] = useState<DicomStudy[]>([])
    const [filteredStudies, setFilteredStudies] = useState<DicomStudy[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [modalityFilter, setModalityFilter] = useState('')
    const [selectedStudy, setSelectedStudy] = useState<DicomStudy | null>(null)
    const [openingViewer, setOpeningViewer] = useState<number | null>(null)

    useEffect(() => {
        if (user?.companyId) {
            loadStudies()
        }
    }, [user?.companyId])

    useEffect(() => {
        let filtered = studies

        if (searchTerm.trim()) {
            filtered = filtered.filter(
                study =>
                    study.patientName
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    study.patientId
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    study.accessionNumber
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    study.studyDescription
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
        }

        if (modalityFilter) {
            filtered = filtered.filter(
                study => study.modality === modalityFilter
            )
        }

        setFilteredStudies(filtered)
    }, [searchTerm, modalityFilter, studies])

    const loadStudies = async () => {
        setLoading(true)
        try {
            const response = await getDicomStudies()
            if (response.success) {
                setStudies(response.result)
                setFilteredStudies(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar estudos')
            }
        } catch (error) {
            toast.error('Erro ao carregar estudos')
        } finally {
            setLoading(false)
        }
    }

    const handleOpenViewer = async (id: number) => {
        setOpeningViewer(id)
        try {
            const response = await getStudyViewerUrl(id)
            if (response.success && response.result.url) {
                window.open(response.result.url, '_blank')
            } else {
                toast.error('URL do visualizador nao disponivel')
            }
        } catch (error) {
            toast.error('Erro ao abrir visualizador')
        } finally {
            setOpeningViewer(null)
        }
    }

    const uniqueModalities = [
        ...new Set(studies.map(s => s.modality).filter(Boolean)),
    ]

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando estudos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/health-integrations/pacs')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                            <MdImage className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                Estudos DICOM
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {studies.length}{' '}
                                {studies.length === 1 ? 'estudo' : 'estudos'}
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={loadStudies}
                    className="btn btn-ghost btn-sm gap-2"
                >
                    <MdRefresh className="text-xl" />
                    Atualizar
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente, ID ou descricao..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <MdFilterList className="text-xl text-base-content/60" />
                        <select
                            className="select select-bordered"
                            value={modalityFilter}
                            onChange={e => setModalityFilter(e.target.value)}
                        >
                            <option value="">Todas as modalidades</option>
                            {uniqueModalities.map(modality => (
                                <option key={modality} value={modality}>
                                    {DICOM_MODALITY_SHORT_LABELS[
                                        modality as DicomModality
                                    ] || modality}
                                </option>
                            ))}
                        </select>
                    </div>

                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredStudies.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdImage className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhum estudo encontrado
                        </h3>
                        <p className="text-sm text-base-content/60">
                            {searchTerm || modalityFilter
                                ? 'Tente ajustar seus filtros'
                                : 'Estudos DICOM aparecerao aqui'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Paciente</th>
                                    <th>Modalidade</th>
                                    <th>Descrição</th>
                                    <th>Data</th>
                                    <th>Series</th>
                                    <th>Servidor</th>
                                    <th>Acoes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudies.map(study => (
                                    <tr key={study.id} className="hover">
                                        <td>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <MdPerson className="text-lg text-base-content/60" />
                                                    <span className="font-medium">
                                                        {study.patientName ||
                                                            '-'}
                                                    </span>
                                                </div>
                                                {study.patientId && (
                                                    <span className="text-xs text-base-content/60">
                                                        ID: {study.patientId}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span
                                                className="badge badge-outline"
                                                title={
                                                    DICOM_MODALITY_LABELS[
                                                        study.modality as DicomModality
                                                    ] || study.modality
                                                }
                                            >
                                                {DICOM_MODALITY_SHORT_LABELS[
                                                    study.modality as DicomModality
                                                ] || study.modality}
                                            </span>
                                        </td>
                                        <td className="max-w-xs truncate">
                                            {study.studyDescription || '-'}
                                        </td>
                                        <td>
                                            {study.studyDate
                                                ? new Date(
                                                      study.studyDate
                                                  ).toLocaleDateString('pt-BR')
                                                : '-'}
                                        </td>
                                        <td>
                                            <span className="badge badge-sm">
                                                {study.numberOfSeries || 0}{' '}
                                                series
                                            </span>
                                        </td>
                                        <td>{study.server?.name || '-'}</td>
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() =>
                                                        setSelectedStudy(study)
                                                    }
                                                    className="btn btn-ghost btn-sm"
                                                    title="Ver detalhes"
                                                >
                                                    <MdVisibility className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        handleOpenViewer(
                                                            study.id
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-sm text-secondary"
                                                    title="Abrir no visualizador"
                                                    disabled={
                                                        openingViewer ===
                                                        study.id
                                                    }
                                                >
                                                    {openingViewer ===
                                                    study.id ? (
                                                        <span className="loading loading-spinner loading-sm"></span>
                                                    ) : (
                                                        <MdOpenInNew className="text-lg" />
                                                    )}
                                                </button>
                                                {study.client && (
                                                    <span
                                                        className="badge badge-success badge-sm"
                                                        title="Vinculado a paciente"
                                                    >
                                                        <MdLink className="text-xs" />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Study Detail Modal */}
            {selectedStudy && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-3xl">
                        <h3 className="font-bold text-lg mb-4">
                            Detalhes do Estudo
                        </h3>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Paciente
                                    </label>
                                    <p className="font-medium">
                                        {selectedStudy.patientName || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        ID do Paciente
                                    </label>
                                    <p className="font-medium">
                                        {selectedStudy.patientId || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Data de Nascimento
                                    </label>
                                    <p className="font-medium">
                                        {selectedStudy.patientBirthDate
                                            ? new Date(
                                                  selectedStudy.patientBirthDate
                                              ).toLocaleDateString('pt-BR')
                                            : '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Modalidade
                                    </label>
                                    <p className="font-medium">
                                        {DICOM_MODALITY_LABELS[
                                            selectedStudy.modality as DicomModality
                                        ] || selectedStudy.modality}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Data do Estudo
                                    </label>
                                    <p className="font-medium">
                                        {selectedStudy.studyDate
                                            ? new Date(
                                                  selectedStudy.studyDate
                                              ).toLocaleDateString('pt-BR')
                                            : '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Numero de Series
                                    </label>
                                    <p className="font-medium">
                                        {selectedStudy.numberOfSeries || 0}
                                    </p>
                                </div>
                            </div>

                            <div className="divider"></div>

                            <div>
                                <label className="text-sm text-base-content/60">
                                    Descrição
                                </label>
                                <p className="font-medium">
                                    {selectedStudy.studyDescription || '-'}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Accession Number
                                    </label>
                                    <p className="font-medium font-mono">
                                        {selectedStudy.accessionNumber || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Study Instance UID
                                    </label>
                                    <p className="font-medium font-mono text-xs break-all">
                                        {selectedStudy.studyInstanceUid}
                                    </p>
                                </div>
                            </div>

                            {selectedStudy.client && (
                                <>
                                    <div className="divider"></div>
                                    <div>
                                        <label className="text-sm text-base-content/60">
                                            Paciente Vinculado
                                        </label>
                                        <p className="font-medium">
                                            {selectedStudy.client.name}
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-secondary"
                                onClick={() =>
                                    handleOpenViewer(selectedStudy.id)
                                }
                            >
                                <MdOpenInNew className="text-lg" />
                                Abrir Visualizador
                            </button>
                            <button
                                className="btn"
                                onClick={() => setSelectedStudy(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setSelectedStudy(null)}>
                            close
                        </button>
                    </form>
                </dialog>
            )}
        </div>
    )
}
